import {
  useContext,
  useEffect,
  useState,
  useRef,
  RefObject,
} from "react";
import { FC, Props } from "./typings";
import styles from "./styles.module.scss";
import PackageCaseModal from "components/PackageCaseModal";
import AddTagModal from "components/AddTagModal";
import { useNavigate, useParams } from "react-router-dom";
import EmailService from "api/emailService";
import { IAttachment } from "dto/IAttachment";
import Avatar from "components/Avatar";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import listService from "api/listService";
import ChatActionsList from "components/ChatActionList";
import CreateActionModal from "components/CreateActionModal";
import ModalsManagerContext from "contexts/ModalsManager";
import IconsState from "components/IconsState";
import PackagesContext from "contexts/PackagesContext";
import { useAuth } from "react-oidc-context";
import { getUserId } from "utils/getUserId";
import { addToUrl, parseHTML } from "utils/parseHTML";
import { sanitizeUserHtml } from "utils/sanitizeUserHtml";
import { getKeys } from "utils/getKeys";
import EmailViewer from "components/EmailViewer";
import GroupIcon from "assets/icons/groups.svg?react";
import ModalLayout, { DockPosition } from "components/ModalLayout";
import PackageReceiversModal from "components/PackageReceiversModal";
import OptionsCard from "components/OptionsCard/OptionsCard";
import Worker from "./worker?worker";
import {
  getContentKeysGeneration,
  getContentKeysSnapshot,
  onContentKeysChanged,
} from "utils/contentKeys";
import PackageAttachment from "components/PackageAttachment";
import PackageThreadContext from "contexts/PackageThreadContext";
import {
  faAngleDown,
  faAngleUp,
} from "@fortawesome/free-solid-svg-icons";
import usePDF from "hooks/usePDF";
import { ItemKind } from "dto/IKindItems";
import { IAttachToCaseFolderLink } from "api/casesService";
import AttachToCaseModalAttachments from "components/AttachCaseModalNew/AttachToCaseModalAttachments";
import PendingPaymentModal from "components/PendingPaymentModal";
import settingsContext, { IOptionType } from "contexts/SettingsContext";
import ReplyIcon from "assets/icons/reply_all.svg?react";
import ForwardIcon from "assets/icons/forward.svg?react";
import Popper from "components/Popper";
import KeysContext from "contexts/KeysContext";
import { useTranslation } from "react-i18next";

const ModalWrapper = ({
  refWrapper,
  children,
}: {
  refWrapper: RefObject<HTMLDivElement>;
  children: any;
}) => {
  if (!refWrapper?.current) return <></>;
  const position = refWrapper.current!.getBoundingClientRect();
  const x = position.x;
  const y = position.y + 45;

  return ReactDOM.createPortal(
    <div
      style={{
        position: "absolute",
        left: `${x}px`,
        top: `${y}px`,
        zIndex: 1000,
      }}
    >
      {children}
    </div>,
    document.body,
  );
};

const PackThreadRenderer: FC<Props> = ({
  pack,
  refetch,
  onPreview,
  onDownload,
  isLast,
  setSelectedElements,
}) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const userId = getUserId(auth.user!);
  const { keys } = useContext(KeysContext);

  const {
    filterId,
    status: linkStatus,
    threadPackageId,
  } = useParams();
  const navigate = useNavigate();

  const { modals } = useContext(ModalsManagerContext);
  const anyModalOpen =
    modals.settings ||
    modals.addAddress ||
    modals.addClient ||
    modals.attachCase ||
    modals.createAction ||
    modals.createCase ||
    modals.createChat ||
    modals.createCompany ||
    modals.createDossier ||
    modals.createEntity ||
    modals.createEvent ||
    modals.createHelpdesk ||
    modals.createInvoice ||
    modals.createLetter ||
    modals.createPost ||
    modals.createPostage ||
    modals.createTask;
  const { checkIsAllowed, showNotSupportedNotification } = usePDF();
  const { threadPackages } =
    useContext(PackageThreadContext);

  const [showFullContent, setShowFullContent] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const { data: previewData, isLoading: isPreviewDataLoading } =
    EmailService.useGetPreview(
      pack.Id ?? "",
      showFullContent,
      pack?.Kind === 128,
    );

  const [attachmentsLocation, setAttachmentsLocation] =
    useState<string>("center");

  const { options: settings } = useContext(settingsContext);

  useEffect(() => {
    if (settings) {
      const attL = settings.find(
        (x) => x.Type === IOptionType.caseAttachAttachmentsLocation,
      );

      if (attL) {
        setAttachmentsLocation(attL.Value);
      }
    }
  }, [settings]);

  const [fullContent, setFullContent] = useState<null | string>(null);
  const [content, setContent] = useState<null | string>(null);
  const [footerContent, setFooterContent] = useState<null | string>(null);
  const [showCase, setShowCase] = useState(false);
  const [showReceivers, setShowReceivers] = useState(false);
  const [showCcReceivers, setShowCcReceivers] = useState(false);
  const [, setOptions] = useState(false);

  const [showFooter, setShowFooter] = useState(false);

  const [createAction, setCreateAction] = useState(false);
  const [showActionList, setShowActionList] = useState(false);

  const refWrapper = useRef(null);
  const [showActions, setShowActions] = useState(false);
  const [showCreateAction, setShowCreateAction] = useState(false);
  const [showTags, setShowTags] = useState(false);

  const ref = useRef<HTMLDivElement>(null);
  const receiversReference = useRef(null);
  const receiversCcReference = useRef(null);
  const [position, setPosition] = useState([0, 0]);

  const packCcReceivers = pack?.Receivers?.filter((x) => x.Cc || x.Bcc);
  const packReceivers = pack?.Receivers?.filter((x) => !x.Cc && !x.Bcc);

  // Przesyłka przychodząca w wątku zablokowana do czasu zwiększenia pakietu — limit
  // darmowych przesyłek (payment) lub brak miejsca na dysku (storage). Otoczka i nagłówek
  // wiadomości zostają, ale zamiast treści pokazujemy informację o blokadzie, a załączniki chowamy.
  const isBlocked =
    !!(pack?.IsPendingPayment || pack?.IsPendingStorage) && pack.IsIncoming;
  const blockVariant = pack?.IsPendingPayment ? "payment" : "storage";

  const onNewWindow = async (attach: IAttachment) =>
    window.open(
      `${
        import.meta.env.VITE_APP_SITE ?? "https://alfa.pocztaprawnicza.pl"
      }/document?pdf=${pack.Id}:${attach.FileName.replace(":", "")}:${
        attach.Uri ?? attach.Id
      }:${attach.Kind}:${attach.EncryptionKeyId}`,
    );

  useEffect(() => {
    setContent(null);
  }, [pack]);

  // Klucze firmy wjeżdżają do mirrora ASYNCHRONICZNIE (App.tsx odszyfrowuje grant przy
  // starcie sesji). Podgląd otwarty wprost z adresu potrafi wyrenderować się wcześniej —
  // dostałby wtedy pustą migawkę i skończył na „Nie można odszyfrować treści". Licznik
  // generacji mirrora wymusza ponowienie deszyfracji, gdy klucze już są.
  const [contentKeysGeneration, setContentKeysGeneration] = useState(
    getContentKeysGeneration,
  );
  useEffect(() => onContentKeysChanged(setContentKeysGeneration), []);

  useEffect(() => {
    if (!previewData && !keys) return;

    if (
      !previewData &&
      pack &&
      pack.Kind !== ItemKind.Email &&
      pack.Kind !== ItemKind.ContactMessage &&
      (pack.Kind !== ItemKind.OutgoingParcel || !pack.EncryptionKeyId)
    ) {
      setFullContent(pack.S2);
      parseHTML(pack.S2, [], false).then((r) => {
        console.log("Parsed", r);
        setContent(r.content);
        setHasMore(r.hasMore);
      });
      return;
    }

    const worker = new Worker();
    (async () => {
      worker.postMessage({
        pack: JSON.stringify(pack),
        myKeys: JSON.stringify(await getKeys(userId!)),
        keys: JSON.stringify(keys),
        userId: userId!,
        previewData: JSON.stringify(previewData),
        // Migawka mirrora kluczy TREŚCI — worker ma własną instancję modułów, więc bez niej
        // startuje z pustym mirrorem i ma do dyspozycji wyłącznie klucz intermediate.
        // Klucze obiektów są dziś owinięte kluczem FIRMY, którego intermediate nie rozwinie.
        contentKeys: getContentKeysSnapshot(),
      });
      worker.onmessage = async (event) => {
        const data = JSON.parse(event.data);

        // Porażka deszyfracji w workerze — zamiast wiecznego spinnera pokazujemy komunikat.
        if (data.failed) {
          setContent(
            '<div style="padding:12px 16px;color:#79767a;font-style:italic;line-height:1.5">Nie można odszyfrować treści tej przesyłki.</div>',
          );
          return;
        }

        if (!data.encrypted) {
          setContent(await addToUrl(previewData?.data?.Content, true));
          setHasMore(previewData?.data.HasMore);
          return;
        }

        setContent((await parseHTML(data.content, data.images)).content);
        setHasMore(previewData?.data?.HasMore);
      };
      // Nieoczekiwany błąd workera (wyjątek nieobsłużony) — też gasimy spinner.
      worker.onerror = () => {
        setContent(
          '<div style="padding:12px 16px;color:#79767a;font-style:italic;line-height:1.5">Nie można odszyfrować treści tej przesyłki.</div>',
        );
      };
    })();

    return () => {
      worker.terminate();
    };
  }, [pack, keys, userId, previewData, contentKeysGeneration]);

  useEffect(() => {
    if (!previewData && !keys) return;

    if (!showFooter) return;

    if (!previewData.data.Footer) return;

    const worker = new Worker();
    (async () => {
      worker.postMessage({
        pack: JSON.stringify(pack),
        myKeys: JSON.stringify(await getKeys(userId!)),
        keys: JSON.stringify(keys),
        userId: userId!,
        previewData: JSON.stringify(previewData),
        footer: true,
        // Patrz komentarz wyżej — stopka jedzie tym samym workerem i wymaga tych samych kluczy.
        contentKeys: getContentKeysSnapshot(),
      });
      worker.onmessage = async (event) => {
        const data = JSON.parse(event.data);

        setFooterContent((await parseHTML(data.content, data.images)).content);
      };
    })();

    return () => {
      worker.terminate();
    };
  }, [showFooter]);

  useEffect(() => {
    if (fullContent && pack && pack.Kind !== ItemKind.Email) {
      parseHTML(fullContent, [], showFullContent).then((r) => {
        setContent(r.content);
      });
    }
  }, [showFullContent]);

  useEffect(() => {
    if (!ref?.current) return;

    const rect = ref.current.getBoundingClientRect();
    setPosition([rect.x + rect.width / 2, rect.y + 40]);
  }, [ref, pack]);

  const handleChangeView = () => {
    if (showActionList) {
      setShowActionList(false);
      setCreateAction(true);
      return;
    }
    setShowActionList(true);
    setCreateAction(false);
  };

  const refe = useRef(null);
  const optionsRef = useRef(null);

  const { mutate: manageDossier } = listService.useManageDossier();

  const { packages, setPackages } = useContext(PackagesContext);

  const [showDossier, setShowDossier] = useState<boolean>(false);
  const [usedAttachments, setUsedAttachments] = useState<
    IAttachToCaseFolderLink[]
  >([]);
  const [unusedAttachments, setUnusedAttachments] = useState<IAttachment[]>([]);

  const handleAddOnePackage = (attachment: IAttachment) => {
    console.log("adding");
    setUsedAttachments([]);
    setUnusedAttachments(pack.Attachments);
    setShowDossier(true);
  };

  const handleDossierSave = () => {
    manageDossier({
      AttachmentIds: usedAttachments.map((x) => x.AttachmentId),
      OwnerId: pack.Id,
      OwnerKind: pack.Kind,
      Show: true,
      Links: usedAttachments,
    });

    const newPacks = [...packages];

    usedAttachments.forEach((a) => {
      const el = newPacks.find((p) => p.Id === pack.Id);
      if (el) {
        const attach = el.Attachments.find((e) => e.Id === a.AttachmentId);

        if (attach) {
          attach.ShowInDossier = 1;
        }
      }
    });

    setPackages(newPacks);
  };

  const getPosition = (refWrapper: RefObject<HTMLDivElement>) => {
    if (!refWrapper?.current) return { x: 0, y: 0, width: 0, height: 0 };
    const position = refWrapper.current!.getBoundingClientRect();

    return {
      x: position.x ?? 0,
      y: position.y ?? 0,
      width: position.width,
      height: position.height,
    };
  };

  const isIncoming = linkStatus === "incoming";
  const link = linkStatus
    ? linkStatus
    : isIncoming === true
    ? "incoming"
    : "outcoming";

  const handleMessageClick = () => {
    if (pack?.Thread?.Id) {
      setSelectedElements([pack]);
      navigate(
        `/packages/thread/${filterId}/${link}/${pack.Kind}/${pack.Thread.Id}/${pack.Id}`,
      );

      return;
    }
  };

  // useEffect(() => {
  //   if (!ref?.current) return;
  //   if (threadPackageId === pack.Id) {
  //     ref.current.scrollIntoView({
  //       behavior: "smooth",
  //       block: "nearest",
  //       inline: "nearest",
  //     });
  //   }
  // }, [threadPackageId, parent, ref]);

  return (
    <div
      ref={ref}
      onMouseEnter={() => setOptions(true)}
      onMouseLeave={() => setOptions(false)}
      className={`${styles.wrapper} ${
        threadPackageId === pack.Id && styles.activeWrapper
      }`}
    >
      {threadPackageId === pack.Id && !anyModalOpen && (
        <Popper
          closeOnClickAway={false}
          autoHide={false}
          anchorEl={ref?.current}
          open={true}
          placement="top"
          overlay={false}
        >
          <div className={styles.optionsbar}>
            <OptionsCard
              rightSide={true}
              toggle={() => {
                // setShowOptions(false);
                // setIsRightClicked(false);
              }}
              isRightClicked={false}
              binds
              selectedElements={[]}
              setSelectedElements={() => {}}
              showOptions={true}
              pack={pack}
              refetch={() => {}}
              refWrapper={ref}
              onClickAttachIcon={() => {}}
            />
          </div>
        </Popper>
      )}
      <div className={styles.content}>
        {showActionList && (
          <ChatActionsList
            toggle={setShowActionList}
            changeView={handleChangeView}
            x={position[0]}
            y={position[1]}
            pack={pack}
          />
        )}
        {createAction && (
          <CreateActionModal
            kind={pack.Kind}
            initialValue={{
              parcelId: pack.Id,
            }}
            showCase={false}
            refetch={refetch}
            toggle={() => setCreateAction(false)}
            x={position[0]}
            y={position[1]}
          />
        )}
        {showCase && (
          <PackageCaseModal toggle={setShowCase} CaseId={pack?.Case?.Id} />
        )}

        {showTags && (
          <ModalWrapper refWrapper={refWrapper}>
            <AddTagModal
              toggle={setShowTags}
              updateTags={refetch}
              toTag={pack}
            />
          </ModalWrapper>
        )}
        {showActions && (
          <ModalWrapper refWrapper={refWrapper}>
            <div className={styles.modalee}>
              <ChatActionsList
                changeView={() => {
                  setShowActions(false);
                  setShowCreateAction(true);
                }}
                toggle={() => {}}
                x={getPosition(refWrapper).x}
                y={getPosition(refWrapper).y + 45}
                pack={pack}
              />
            </div>
          </ModalWrapper>
        )}
        {showCreateAction && (
          <ModalWrapper refWrapper={refWrapper}>
            <div className={styles.modalee}>
              <CreateActionModal
                kind={pack.Kind}
                initialValue={{
                  parcelId: pack.Id,
                }}
                showCase={false}
                refetch={() => {}}
                toggle={() => setShowCreateAction(false)}
                x={getPosition(refWrapper).x}
                y={getPosition(refWrapper).y + 45}
              />
            </div>
          </ModalWrapper>
        )}

        {showDossier && (
          <>
            {attachmentsLocation === "center" && (
              <ModalLayout dock={DockPosition.CENTER} showBg={true}>
                <AttachToCaseModalAttachments
                  toggle={(c) => setShowDossier(c)}
                  c={pack.Case}
                  pack={pack}
                  unusedAttachments={unusedAttachments}
                  setUnusedAttachments={setUnusedAttachments}
                  setUsedAttachments={setUsedAttachments}
                  usedAttachments={usedAttachments}
                  onSave={handleDossierSave}
                  position="center"
                ></AttachToCaseModalAttachments>
              </ModalLayout>
            )}
            {attachmentsLocation !== "center" && (
              <ModalLayout
                dock={DockPosition.DISABLED}
                off={true}
                customX={8}
                customY={0}
                ignore
                isAnimation
                fullHeight={true}
                name="attachCase"
              >
                <AttachToCaseModalAttachments
                  toggle={(c) => setShowDossier(c)}
                  c={pack.Case}
                  pack={pack}
                  unusedAttachments={unusedAttachments}
                  setUnusedAttachments={setUnusedAttachments}
                  setUsedAttachments={setUsedAttachments}
                  usedAttachments={usedAttachments}
                  onSave={handleDossierSave}
                  className={styles.nomodal}
                  position="left"
                ></AttachToCaseModalAttachments>
              </ModalLayout>
            )}
          </>
        )}

        <div className={styles.personWrapper} onClick={handleMessageClick}>
          <div>
            <div className={styles.direction}>
              {!pack.IsIncoming && !pack.IsForwarded && !isLast && (
                <div className={styles.directionIcon}>
                  <ReplyIcon /> {t("packages.reply")}
                </div>
              )}
              {((!pack.IsIncoming && pack.IsForwarded) || pack.Pass) && (
                <div className={styles.directionIcon}>
                  <ForwardIcon /> {t("packages.forwarded")}
                </div>
              )}
            </div>

            <div className={styles.person}>
              <Avatar className={styles.avastar} name={pack.Sender ?? ""} />
              <div className={styles.senderWrapper}>
                <div className={styles.nameAndDate}>
                  <div className={styles.name}>{pack?.Sender}</div>
                  <div className={styles.date}>
                    {moment(pack.ReceiveDate ?? pack.CreationDate)
                      .format("DD.MM.YYYY")
                      .toString()}
                  </div>
                </div>
                <div className={styles.titleWrapper}>
                  <div className={styles.titlePack}>{pack.S1}</div>
                  {/* <div
                    style={{
                      position: "absolute",
                      right: "0px",
                      top: "37px",
                      width: "32px",
                      height: "32px",
                    }}
                    ref={optionsRef}
                  >
                    <OptionsCard
                      thread={true}
                      refWrapper={optionsRef}
                      pack={pack}
                      // isRightClicked
                      selectedElements={[]}
                      showOptions={true}
                      className={styles.opt}
                      rightSide
                      onClickAttachIcon={() => {}}
                      refetch={refetch}
                      debug
                    />
                  </div> */}
                  <div ref={refe} className={styles.icons}>
                    <IconsState
                      onPreview={onPreview}
                      onDownload={onDownload}
                      customY={-30}
                      keys={keys}
                      msg={
                        packages.find((p) => p.Id === pack.Id) ??
                        threadPackages.find((p) => p.Id === pack.Id) ??
                        pack
                      }
                    />
                  </div>
                </div>
                <div className={styles.toWrapper}>
                  <div className={styles.to}>
                    Do:
                    <div className={styles.toList}>
                      {packReceivers && packReceivers.length > 0 && (
                        <div className={styles.receiver}>
                          {packReceivers[0].Name?.length
                            ? packReceivers[0].Name
                            : packReceivers[0].Email}
                        </div>
                      )}
                      {packReceivers && packReceivers.length > 1 && (
                        <>
                          <div
                            className={styles.moreReceivers}
                            ref={receiversReference}
                            onClick={() => setShowReceivers(!showReceivers)}
                          >
                            <GroupIcon></GroupIcon> + {packReceivers.length - 1}
                          </div>

                          {showReceivers && (
                            <ModalLayout refWrapper={receiversReference}>
                              <PackageReceiversModal
                                toggle={() => setShowReceivers(false)}
                                pack={pack}
                                cc={false}
                              />
                            </ModalLayout>
                          )}
                        </>
                      )}
                    </div>
                    {packCcReceivers && packCcReceivers.length > 0 && (
                      <div className={styles.to}>
                        Dw:
                        <div className={styles.toList}>
                          {packCcReceivers && packCcReceivers.length > 0 && (
                            <div className={styles.receiver}>
                              {packCcReceivers[0].Name ??
                                packCcReceivers[0].Email}
                            </div>
                          )}
                          {packCcReceivers && packCcReceivers.length > 1 && (
                            <>
                              <div
                                className={styles.moreReceivers}
                                ref={receiversCcReference}
                                onClick={() =>
                                  setShowCcReceivers(!showCcReceivers)
                                }
                              >
                                <GroupIcon></GroupIcon> +{" "}
                                {packCcReceivers.length - 1}
                              </div>

                              {showCcReceivers && (
                                <ModalLayout refWrapper={receiversCcReference}>
                                  <PackageReceiversModal
                                    toggle={() => setShowCcReceivers(false)}
                                    pack={pack}
                                    cc={true}
                                  />
                                </ModalLayout>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className={styles.expandersWrapper}>
                  {!isBlocked && (previewData?.data || hasMore) && (
                    <div className={styles.expander}>
                      {previewData?.data.HasFooter && !showFullContent && (
                        <div
                          className={styles.expandButton}
                          onClick={() => {
                            setShowFooter(!showFooter);
                          }}
                        >
                          {showFooter ? t("packages.collapseFooter") : t("packages.expandFooter")}
                          <FontAwesomeIcon
                            icon={showFooter ? faAngleUp : faAngleDown}
                          />
                        </div>
                      )}
                      {hasMore && (
                        <div
                          className={styles.expandButton}
                          onClick={() => {
                            setShowFullContent(!showFullContent);
                          }}
                        >
                          {showFullContent
                            ? t("packages.collapseContent")
                            : t("packages.expandContent")}
                          <FontAwesomeIcon
                            icon={showFullContent ? faAngleUp : faAngleDown}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.borderLine}>
          <div className={styles.line}></div>
        </div>
        {isBlocked ? (
          <PendingPaymentModal pack={pack} variant={blockVariant} />
        ) : (
          <>
            {(content === "" || content === null || isPreviewDataLoading) && (
              <div className={styles.contentLoader}>
                {t("packages.loadingContent")}
              </div>
            )}

            {content && !isPreviewDataLoading && (
              <EmailViewer sourceContent={content ?? ""}></EmailViewer>
            )}
            {/* {content && showContent && (
          <div
            dangerouslySetInnerHTML={{ __html: content }}
            className={styles.body}
          ></div>
        )} */}

            {footerContent && showFooter && (
              <div
                dangerouslySetInnerHTML={{
                  __html: sanitizeUserHtml(footerContent),
                }}
                className={styles.body}
              ></div>
            )}
          </>
        )}

        {!isBlocked && (
          <div className={styles.attachments}>
            {pack.Attachments?.map((attachment) => (
            <PackageAttachment
              className={styles.attachment}
              onAddDossier={pack?.Case ? handleAddOnePackage : undefined}
              date={moment(pack.ReceiveDate ?? pack.CreationDate)
                .format("HH:MM | DD.MM.YYYY")
                .toString()}
              onNewWindow={onNewWindow}
              onPreview={(attach) => {
                const isAllowed = checkIsAllowed(attachment.FileName);
                if (!isAllowed) {
                  showNotSupportedNotification();
                  return;
                }
                // onPreview(pack, attach, previewData);
                const newUrl = `${window.location.pathname}?pdf=${
                  pack.Id
                }:${attach.FileName.replace(":", "")}:${
                  attach.Uri ?? attach.Id
                }:${attach.Kind}:${attach.EncryptionKeyId}`;
                navigate(newUrl);
              }}
              onDownload={(attach) => onDownload(pack, attach)}
              attachment={attachment}
            />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PackThreadRenderer;
